import { useEffect, useRef, useState } from 'react'
import { Quote, Lightbulb, Users, Rocket } from 'lucide-react'

export const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.3 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <section
      id="about"
      ref={sectionRef}
      className="section scroll-mt-20 overflow-hidden"
      aria-labelledby="about-heading"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white to-amber-50/30" aria-hidden="true" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-brand-blue/5 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-brand-amber/5 rounded-full blur-3xl" aria-hidden="true" />

      <div className="container-narrow relative">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span
            className={`inline-block px-4 py-1.5 bg-brand-amber/10 text-brand-amber text-sm font-semibold rounded-full mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Our Story
          </span>
          <h2
            id="about-heading"
            className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Who We Are
          </h2>
        </div>

        {/* Main Content */}
        <div
          className={`transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-6 text-center">
            Learning Xperience Consulting bridges the gap between cutting-edge educational
            technology and meaningful learning outcomes. We partner with higher education
            institutions and corporate training teams to design, implement, and optimize
            learning experiences that truly engage.
          </p>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-10 text-center">
            Our approach combines deep expertise in instructional design, emerging technologies
            like AI and immersive learning, and a relentless focus on the learner. We don't
            just implement tools—we transform how organizations think about and deliver education.
          </p>

          {/* Quote Block */}
          <blockquote
            className={`relative bg-gradient-to-br from-slate-50 to-white border-l-4 border-brand-amber rounded-r-2xl p-8 sm:p-10 transition-all duration-700 delay-[400ms] ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <Quote
              size={40}
              className="absolute -top-4 -left-2 text-brand-amber/30"
              aria-hidden="true"
            />
            <p className="text-xl sm:text-2xl text-slate-700 font-medium leading-relaxed mb-6 italic">
              "In the rush to adopt the latest educational technologies, many
              institutions lose sight of what truly matters: the learners."
            </p>
            <footer className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-blue to-brand-amber flex items-center justify-center text-white font-bold text-lg"
                aria-hidden="true"
              >
                G
              </div>
              <div>
                <cite className="not-italic font-semibold text-slate-900">
                  Giovanni
                </cite>
                <p className="text-sm text-slate-500">Founder, LXC</p>
              </div>
            </footer>
          </blockquote>

          {/* Values */}
          <div
            className={`mt-16 grid sm:grid-cols-3 gap-8 transition-all duration-700 delay-[600ms] ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="group text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200/50 shadow-sm hover:shadow-xl hover:border-brand-blue/20 hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand-blue/10 to-brand-blue/5 flex items-center justify-center group-hover:scale-110 group-hover:from-brand-blue group-hover:to-brand-blue-dark transition-all duration-300">
                <Lightbulb size={28} className="text-brand-blue group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-brand-blue mb-3">
                Learner-First
              </div>
              <p className="text-slate-600">
                Every decision we make starts with one question: how does this serve
                the learner? Technology should enhance, not complicate, the learning journey.
              </p>
            </div>
            <div className="group text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200/50 shadow-sm hover:shadow-xl hover:border-brand-amber/20 hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand-amber/10 to-brand-amber/5 flex items-center justify-center group-hover:scale-110 group-hover:from-brand-amber group-hover:to-orange-500 transition-all duration-300">
                <Rocket size={28} className="text-brand-amber group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-brand-amber mb-3">
                Innovation
              </div>
              <p className="text-slate-600">
                We stay at the forefront of EdTech—from AI and adaptive learning to VR
                and gamification—bringing you solutions that create real competitive advantage.
              </p>
            </div>
            <div className="group text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200/50 shadow-sm hover:shadow-xl hover:border-slate-400/20 hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-slate-200/50 to-slate-100 flex items-center justify-center group-hover:scale-110 group-hover:from-slate-700 group-hover:to-slate-800 transition-all duration-300">
                <Users size={28} className="text-slate-600 group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-slate-700 mb-3">
                Partnership
              </div>
              <p className="text-slate-600">
                We're not just consultants—we're collaborators. We work alongside your
                team to build internal capacity and achieve sustainable transformation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
